import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORE_KEY = "scroll-positions";
const MAX_ENTRIES = 60;
const MAX_WAIT_MS = 3000;
const STABLE_FRAMES = 20;
const PIN_STABLE_FRAMES = 30;
const PIN_MAX_MS = 2000;
const USER_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"];

if (typeof window !== "undefined") {
  window.history.scrollRestoration = "manual";
}

const readStore = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
  } catch {
    return {};
  }
};

const savePosition = (key, y) => {
  try {
    const store = readStore();
    delete store[key];
    store[key] = Math.round(y);
    const keys = Object.keys(store);
    keys
      .slice(0, Math.max(0, keys.length - MAX_ENTRIES))
      .forEach((old) => delete store[old]);
    sessionStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {}
};

const currentY = () =>
  window.scrollY ||
  document.scrollingElement?.scrollTop ||
  document.documentElement.scrollTop ||
  document.body.scrollTop ||
  0;

const jumpToTop = () => {
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

export default function ScrollToTop() {
  const { pathname, search, hash, key } = useLocation();
  const navigationType = useNavigationType();
  const entryKey = key === "default" ? `default:${pathname}${search}` : key;
  const live = useRef({ key: entryKey, restoring: false, raf: 0 });

  useEffect(() => {
    const state = live.current;

    const save = () => {
      if (state.restoring) return;
      savePosition(state.key, currentY());
    };

    const onScroll = () => {
      if (state.restoring) return;
      window.cancelAnimationFrame(state.raf);
      const keyAtSchedule = state.key;
      state.raf = window.requestAnimationFrame(() => {
        if (state.key !== keyAtSchedule || state.restoring) return;
        savePosition(keyAtSchedule, currentY());
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") save();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", save);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.cancelAnimationFrame(state.raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", save);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useLayoutEffect(() => {
    const state = live.current;

    window.cancelAnimationFrame(state.raf);
    state.key = entryKey;

    const saved = navigationType === "POP" ? readStore()[entryKey] : undefined;

    if (saved > 0) {
      const root = document.documentElement;
      const startedAt = performance.now();
      let raf = 0;
      let lastHeight = -1;
      let stableFrames = 0;

      state.restoring = true;

      const release = () => {
        window.cancelAnimationFrame(raf);
        USER_EVENTS.forEach((name) =>
          window.removeEventListener(name, onUserInput),
        );
        state.restoring = false;
      };

      function onUserInput() {
        release();
      }

      USER_EVENTS.forEach((name) =>
        window.addEventListener(name, onUserInput, { passive: true }),
      );

      const tick = () => {
        const height = root.scrollHeight;
        const maxScroll = height - window.innerHeight;

        if (maxScroll >= saved && Math.abs(currentY() - saved) > 1) {
          window.scrollTo({ top: saved, left: 0, behavior: "instant" });
        }

        stableFrames = height === lastHeight ? stableFrames + 1 : 0;
        lastHeight = height;

        const settled =
          document.readyState === "complete" &&
          stableFrames >= STABLE_FRAMES &&
          maxScroll >= saved;
        const timedOut = performance.now() - startedAt > MAX_WAIT_MS;

        if (settled || timedOut) {
          release();
          return;
        }

        raf = window.requestAnimationFrame(tick);
      };

      tick();
      return release;
    }

    if (hash) {
      state.restoring = false;
      jumpToTop();
      const r = window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView();
      });
      return () => window.cancelAnimationFrame(r);
    }

    const root = document.documentElement;
    const startedAt = performance.now();
    let raf = 0;
    let lastHeight = -1;
    let stableFrames = 0;

    state.restoring = true;

    const stop = () => {
      window.cancelAnimationFrame(raf);
      USER_EVENTS.forEach((name) => window.removeEventListener(name, stop));
      state.restoring = false;
    };

    USER_EVENTS.forEach((name) =>
      window.addEventListener(name, stop, { passive: true }),
    );

    const pin = () => {
      if (currentY() !== 0) jumpToTop();

      const height = root.scrollHeight;
      stableFrames = height === lastHeight ? stableFrames + 1 : 0;
      lastHeight = height;

      const settled =
        document.readyState === "complete" && stableFrames >= PIN_STABLE_FRAMES;

      if (settled || performance.now() - startedAt > PIN_MAX_MS) {
        stop();
        return;
      }

      raf = window.requestAnimationFrame(pin);
    };

    jumpToTop();
    pin();

    return stop;
  }, [entryKey, pathname, search, hash, navigationType]);

  return null;
}